// Contrato de ingesta de telemetria GPSWOX (Fase A del plan
// docs/PLAN-INGESTA-TELEMETRIA.md). Modela lo que DEVUELVE la plataforma del
// proveedor (GPSWOX v3.7.11, whitelabel de Global GPS/Nixel), no nuestro wire
// format: por eso vive aparte de common.ts y NO se registra en el OpenAPI.
// Solo schemas: la normalizacion, la conversion de hora local y la guarda
// geografica viven en apps/api/src/telemetry/normalization.ts.
//
// Fuentes del shape: docs publicas de GPSWOX + inspeccion real del endpoint
// del mapa (gestion/01_dow/nixel-global-gps-telemetria.md, 2026-09-14).
// Regla de seguridad: el user_api_hash es credencial viva; jamas aparece en
// este codigo, en fixtures ni en ejemplos.
import { z } from "zod";
// --- Piezas basicas ----------------------------------------------------------
// lat/lng vienen como string en /objects/items y como number en otras
// respuestas: aceptamos ambos y coercionamos a numero finito. No usamos
// z.coerce.number() porque Number(null) === 0 y un null silencioso en una
// coordenada seria un dato inventado.
const FlexibleNumber = z
    .union([z.number(), z.string().min(1)])
    .transform((v) => (typeof v === "number" ? v : Number(v)))
    .refine((v) => Number.isFinite(v), { message: "numero no finito" });
// Estados de `online` observados en la instancia real. La plataforma puede
// sumar valores nuevos sin avisar: el schema valida string y este enum queda
// como reference tipada para la logica que discrimina statuss conocidos
// (p.ej. "offline" alimenta E4 sin senal).
export const KNOWN_ONLINE_STATUSES = [
    "online",
    "engine",
    "ack",
    "offline",
];
export const KnownOnlineStatus = z.enum(KNOWN_ONLINE_STATUSES);
// --- Dispositivo crudo -------------------------------------------------------
// Un dispositivo (vehiculo) tal como lo entrega GPSWOX. passthrough: la API
// trae decenas de fields secundarios (icon, altitude, group_id, tail, etc.)
// que toleramos sin modelar, para que un field nuevo no rompa la ingesta.
export const GpswoxDeviceSchema = z
    .object({
    // id del equipo GPS; en Fase B mapea a vehiculo -> trip.
    id: z.number().int(),
    // Formato observado: "NNN - PATENTE" (numero de movil + plate).
    name: z.string(),
    lat: FlexibleNumber,
    lng: FlexibleNumber,
    // Velocidad en km/h.
    speed: z.number(),
    // Rumbo en grados (0-360).
    course: z.number(),
    // Ignicion motor on/off; null en equipos sin cablear la ignicion.
    engine_status: z.boolean().nullish(),
    // Segundos detenido, ya computado por la plataforma; puede faltar.
    stop_duration_sec: z.number().int().nullish(),
    // Validado como string a proposito: ver KNOWN_ONLINE_STATUSES.
    online: z.string(),
    // Instant del ultimo reporte: unix (timestamp) y/o local "DD-MM-YYYY
    // HH:MM:SS" (time). Al menos uno tiene que venir (refine abajo).
    timestamp: z.number().int().nullish(),
    time: z.string().nullish(),
    // Ultimo instante en movimiento (unix), opcional.
    moved_timestamp: z.number().int().nullish(),
    // Odometro en metros, opcional.
    total_distance: z.number().nullish(),
})
    .passthrough()
    .refine((d) => d.timestamp != null || d.time != null, {
    message: "el dispositivo no trae instante: falta timestamp y falta time",
});
// --- Los dos sobres ----------------------------------------------------------
// Sobre 1: GET /api/get_devices (API REST formal de GPSWOX). Devuelve un
// array de grupos, cada uno con sus dispositivos en `items`.
export const GetDevicesResponseSchema = z.array(z
    .object({
    id: z.union([z.number(), z.string()]).nullish(),
    title: z.string().nullish(),
    items: z.array(GpswoxDeviceSchema),
})
    .passthrough());
// Sobre 2: GET /objects/items (el endpoint que alimenta el mapa del portal).
// Paginacion por cursor, 500 por pagina.
export const ObjectsItemsResponseSchema = z
    .object({
    data: z.array(GpswoxDeviceSchema),
    pagination: z
        .object({
        per_page: z.number().int().nullish(),
        next_cursor: z.union([z.string(), z.number()]).nullish(),
    })
        .passthrough()
        .nullish(),
})
    .passthrough();
// --- Historial (get_history) -------------------------------------------------
// GET /api/get_history (rango de fechas + dispositivo): lo minimo defendible
// para el criterio 5 de Fase A (reconstruir el recorrido de un dia). GPSWOX
// agrupa los puntos en tramos (`items` anidados) y en el historial suele usar
// latitude/longitude en vez de lat/lng: aceptamos ambas grafias, todo opcional
// y con passthrough. OJO: esta forma NO esta verificada contra la instancia
// real; se ajusta con la corrida real de Fase A.
export const GpswoxHistoryPointSchema = z
    .object({
    lat: FlexibleNumber.nullish(),
    lng: FlexibleNumber.nullish(),
    latitude: FlexibleNumber.nullish(),
    longitude: FlexibleNumber.nullish(),
    speed: z.number().nullish(),
    time: z.string().nullish(),
    timestamp: z.number().nullish(),
})
    .passthrough();
export const GetHistoryResponseSchema = z
    .object({
    items: z.array(z
        .object({
        // Tipo de tramo (movimiento, detencion, etc.), segun docs.
        status: z.number().int().nullish(),
        items: z.array(GpswoxHistoryPointSchema).nullish(),
    })
        .passthrough()),
})
    .passthrough();
// --- Forma normalizada (tabla telemetria_cruda) ------------------------------
// Lo que persiste la tabla de staging telemetria_cruda: fields en camelCase,
// lat/lng siempre numericos, instante en ISO 8601 UTC y null explicito donde
// el proveedor no trae el dato. Idempotencia por (deviceId, instante).
export const GpswoxTelemetrySchema = z.object({
    deviceId: z.number().int(),
    name: z.string(),
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    speed: z.number(),
    course: z.number(),
    engineStatus: z.boolean().nullable(),
    stopDurationSec: z.number().int().nullable(),
    online: z.string(),
    instant: z.string().datetime(),
    totalDistance: z.number().nullable(),
});
//# sourceMappingURL=telemetry.js.map